// The sign-in and account panels. They're real DOM inputs above the canvas, because browser autofill, one-time
// codes, paste, screen readers and Turnstile all need that. Text only ever goes in as text nodes, never as HTML.

import { PRIVACY_URL } from '../shared/analytics';
import { analytics } from './analytics';
import { input } from './input';
import { AccountInfo, api, ApiError, OwnCode, SiteConfig, testIdentity } from './net';

const TEST_DOMAIN = '@riftcrawl.invalid';
const DEFAULT_TEST_ADDRESS = `tester${TEST_DOMAIN}`;
/** Turnstile has to load from exactly this address. It's only asked for once this panel opens. */
const TURNSTILE_SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

const DAY_MS = 24 * 60 * 60 * 1000;

/** A session's last use is only recorded about once a day, so this says no more than that. */
export function activeText(lastUsed: number, now: number): string {
  const days = Math.floor((now - lastUsed) / DAY_MS);
  if (days <= 0) return 'active today';
  return days === 1 ? 'active yesterday' : `active ${days} days ago`;
}

export interface AuthHost {
  signedIn(): void;
  /** `note` says why, if it's worth saying. */
  signedOut(note?: string): void;
  /** This account has a new address. */
  accountChanged(email: string): void;
  /** Another tab in this browser signed in or out. */
  otherTab(): void;
}

interface Turnstile {
  render(container: HTMLElement, options: Record<string, unknown>): string;
  reset(widget: string): void;
  remove(widget: string): void;
}

type View = 'address' | 'code' | 'account' | 'change' | 'changeCode' | 'delete';

const TITLES: Record<View, string> = {
  address: 'Sign in',
  code: 'Sign in',
  account: 'Account',
  change: 'Change address',
  changeCode: 'Change address',
  delete: 'Delete account',
};

/** Typing this is only against an accident. The code to the mailbox is what stops anyone else. */
const DELETE_WORD = 'DELETE';

let turnstileLoading: Promise<Turnstile> | null = null;

function loadTurnstile(): Promise<Turnstile> {
  turnstileLoading ??= new Promise<Turnstile>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT;
    script.onload = () => resolve((window as unknown as { turnstile: Turnstile }).turnstile);
    script.onerror = () => {
      script.remove();
      turnstileLoading = null;
      reject(new Error('Turnstile did not load'));
    };
    document.head.appendChild(script);
  });
  return turnstileLoading;
}

/** Makes an element. Strings among the children become text nodes. */
function el<K extends keyof HTMLElementTagNameMap>(tag: K, props: Partial<HTMLElementTagNameMap[K]> = {}, ...children: (Node | string)[]): HTMLElementTagNameMap[K] {
  const node = Object.assign(document.createElement(tag), props);
  for (const child of children) node.appendChild(typeof child === 'string' ? document.createTextNode(child) : child);
  return node;
}

/** Tells other tabs about sign-in and sign-out. Firefox refuses to make one when cookies are blocked. */
function openTabChannel(): BroadcastChannel | null {
  try {
    return new BroadcastChannel(`riftcrawl-auth-${testIdentity() ?? ''}`);
  } catch {
    return null;
  }
}

const errorText = (e: unknown) => (e instanceof ApiError ? e.message : "Can't reach the server. Check your connection and try again.");

const digits = (field: HTMLInputElement) => field.value.replace(/\D/g, '');

/** Why a request failed, in a form that charts well. The server's message can quote the player's address, so it stays out. */
function failure(e: unknown): Record<string, unknown> {
  return e instanceof ApiError ? { status: e.status, code: e.code } : { status: 0, code: 'network' };
}

export class AuthPanel {
  private overlay = document.getElementById('overlay') as HTMLElement;
  private view: View = 'address';
  private config: SiteConfig | null = null;
  private widget: string | null = null;
  private widgetAction: string | null = null;
  private turnstileToken: string | null = null;
  private email = '';
  private busy = false;
  /** Whether this sign-in made a new account, and how it got there. */
  private signInResult: { method: 'email' | 'test'; isNew: boolean } | null = null;
  /** `?as=` signs in by itself once per page. After that, the player has chosen to be signed out. */
  private autoDone = false;
  private resendTimer: ReturnType<typeof setInterval> | null = null;
  private tabs = openTabChannel();

  private note = el('p', { className: 'note', hidden: true });
  private error = el('p', { className: 'error', hidden: true, role: 'alert' });
  private check = el('div', { className: 'check' });

  private addressInput = el('input', { type: 'email', name: 'email', required: true, autocapitalize: 'none', spellcheck: false });
  private sendButton = el('button', { type: 'submit', className: 'main' }, 'Send code');
  private testButton = el('button', { type: 'button', hidden: true }, 'Test sign-in');
  private addressForm = el(
    'form',
    { noValidate: true },
    el('p', {}, "Sign in to play. No password, we'll email you a code."),
    el('label', {}, 'Email', this.addressInput),
    this.sendButton,
    this.testButton,
    el('p', { className: 'small' }, el('a', { href: PRIVACY_URL, target: '_blank', rel: 'noopener' }, 'Privacy policy')),
  );

  private sentTo = el('b');
  private codeInput = el('input', { type: 'text', name: 'code', inputMode: 'numeric', autocomplete: 'one-time-code' });
  private testCode = el('p', { className: 'test', hidden: true });
  private verifyButton = el('button', { type: 'submit', className: 'main' }, 'Sign in');
  private resendButton = el('button', { type: 'button' });
  private differentButton = el('button', { type: 'button' }, 'Use a different address');
  private codeForm = el(
    'form',
    { noValidate: true },
    el('p', {}, 'Enter the code we sent to ', this.sentTo),
    this.testCode,
    el('label', {}, 'Code', this.codeInput),
    this.verifyButton,
    this.resendButton,
    this.differentButton,
  );

  private accountEmail = el('b');
  private devices = el('ul', { className: 'devices' });
  private accountNews = el('p', { className: 'news', hidden: true });
  private changeAddressButton = el('button', { type: 'button' }, 'Change address');
  private signOutOthersButton = el('button', { type: 'button', hidden: true }, 'Sign out other devices');
  private signOutButton = el('button', { type: 'button' }, 'Sign out');
  private deleteAccountButton = el('button', { type: 'button', className: 'danger' }, 'Delete account');
  private closeButton = el('button', { type: 'button', className: 'main' }, 'Close');
  private accountView = el(
    'div',
    {},
    el('p', {}, 'Signed in as ', this.accountEmail),
    this.devices,
    this.accountNews,
    this.changeAddressButton,
    this.signOutOthersButton,
    this.signOutButton,
    this.deleteAccountButton,
    this.closeButton,
  );
  /** Which opening of the account view is current, so that a slow answer can't fill in a later one. */
  private accountLoads = 0;

  private changeSentTo = el('b');
  private changeCodeInput = el('input', { type: 'text', name: 'change-code', inputMode: 'numeric', autocomplete: 'one-time-code' });
  private newEmailInput = el('input', { type: 'email', name: 'new-email', autocomplete: 'email', autocapitalize: 'none', spellcheck: false });
  private changeTest = el('p', { className: 'test', hidden: true });
  private changeSendButton = el('button', { type: 'submit', className: 'main' }, 'Send code to the new address');
  private changeCancelButton = el('button', { type: 'button' }, 'Cancel');
  private changeForm = el(
    'form',
    { noValidate: true },
    el('p', {}, 'Enter the code we sent to ', this.changeSentTo, ', and the address to move to.'),
    this.changeTest,
    el('label', {}, 'Code', this.changeCodeInput),
    el('label', {}, 'New address', this.newEmailInput),
    this.changeSendButton,
    this.changeCancelButton,
  );

  /** The address this change is moving to. The server has it only as a hash until its mailbox is proven. */
  private newEmail = '';
  private newSentTo = el('b');
  private confirmCodeInput = el('input', { type: 'text', name: 'confirm-code', inputMode: 'numeric', autocomplete: 'one-time-code' });
  private confirmTest = el('p', { className: 'test', hidden: true });
  private alsoSignOut = el('input', { type: 'checkbox', name: 'sign-out-others', checked: true });
  private alsoSignOutRow = el('label', { className: 'tick' }, this.alsoSignOut, 'Also sign out other devices');
  private confirmButton = el('button', { type: 'submit', className: 'main' }, 'Change address');
  private confirmCancelButton = el('button', { type: 'button' }, 'Cancel');
  private confirmForm = el(
    'form',
    { noValidate: true },
    el('p', {}, 'Enter the code we sent to ', this.newSentTo),
    this.confirmTest,
    el('label', {}, 'Code', this.confirmCodeInput),
    this.alsoSignOutRow,
    this.confirmButton,
    this.confirmCancelButton,
  );

  private deleteSentTo = el('b');
  private deleteCodeInput = el('input', { type: 'text', name: 'delete-code', inputMode: 'numeric', autocomplete: 'one-time-code' });
  private deleteWordInput = el('input', { type: 'text', name: 'delete-word', autocomplete: 'off', autocapitalize: 'characters', spellcheck: false });
  private deleteTest = el('p', { className: 'test', hidden: true });
  private deleteButton = el('button', { type: 'submit', className: 'danger' }, 'Delete my account');
  private deleteCancelButton = el('button', { type: 'button', className: 'main' }, 'Cancel');
  private deleteForm = el(
    'form',
    { noValidate: true },
    el('p', {}, 'This deletes your account and all your heroes, for good. Nobody can bring them back.'),
    el('p', {}, 'Enter the code we sent to ', this.deleteSentTo),
    this.deleteTest,
    el('label', {}, 'Code', this.deleteCodeInput),
    el('label', {}, `Type ${DELETE_WORD} to confirm`, this.deleteWordInput),
    this.deleteButton,
    this.deleteCancelButton,
  );

  private title = el('h1', { id: 'overlay-title' });

  constructor(private host: AuthHost) {
    // Autofill matches on this exact token list, and the DOM property's type doesn't allow it.
    this.addressInput.setAttribute('autocomplete', 'username email');
    this.overlay.setAttribute('role', 'dialog');
    this.overlay.setAttribute('aria-modal', 'true');
    this.overlay.setAttribute('aria-labelledby', this.title.id);
    this.overlay.appendChild(el('div', { className: 'panel' }, this.title, this.note, this.addressForm, this.codeForm, this.accountView, this.changeForm, this.confirmForm, this.deleteForm, this.check, this.error));

    this.addressForm.addEventListener('submit', (e) => {
      e.preventDefault();
      void this.send(this.addressInput.value.trim());
    });
    this.testButton.addEventListener('click', () => {
      const typed = this.addressInput.value.trim();
      void this.signInAsTest(typed.endsWith(TEST_DOMAIN) ? typed : DEFAULT_TEST_ADDRESS);
    });
    this.codeForm.addEventListener('submit', (e) => {
      e.preventDefault();
      void this.verify();
    });
    // A pasted or autofilled code is complete, so it can go straight away. "123 456" counts as six digits.
    this.codeInput.addEventListener('input', () => {
      if (this.codeInput.value.replace(/\D/g, '').length === 6) void this.verify();
    });
    this.resendButton.addEventListener('click', () => void this.send(this.email));
    this.differentButton.addEventListener('click', () => this.setView('address'));
    this.signOutOthersButton.addEventListener('click', () => void this.signOutOthers());
    this.signOutButton.addEventListener('click', () => void this.signOut());
    this.closeButton.addEventListener('click', () => this.close());
    this.changeAddressButton.addEventListener('click', () => void this.startManaging('change'));
    this.deleteAccountButton.addEventListener('click', () => void this.startManaging('delete'));
    this.changeForm.addEventListener('submit', (e) => {
      e.preventDefault();
      void this.sendToNewAddress();
    });
    this.confirmForm.addEventListener('submit', (e) => {
      e.preventDefault();
      void this.confirmNewAddress();
    });
    this.deleteForm.addEventListener('submit', (e) => {
      e.preventDefault();
      void this.confirmDelete();
    });
    for (const cancel of [this.changeCancelButton, this.confirmCancelButton, this.deleteCancelButton]) {
      cancel.addEventListener('click', () => this.backToAccount());
    }
    // On the document, because a button that disables itself while it waits drops the keyboard out of the panel.
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || !this.isOpen || this.signingIn) return;
      // Closing hands the keyboard back, and this same key mustn't then reach the game.
      e.stopPropagation();
      if (this.view === 'account') this.close();
      // A request in flight here could be the one that deletes the account, so it has to be seen through.
      else if (!this.busy) this.backToAccount();
    });
    if (this.tabs) {
      this.tabs.onmessage = (e) => {
        if (e.data === 'out') {
          this.autoDone = true;
          // This tab is already asking to sign in, perhaps with its own reason why.
          if (this.isOpen && this.signingIn) return;
        }
        this.host.otherTab();
      };
    }
  }

  private get isOpen(): boolean {
    return !this.overlay.hidden;
  }

  private get signingIn(): boolean {
    return this.view === 'address' || this.view === 'code';
  }

  /** What the robot check on this view is for. Null if there's none here. */
  private get checkAction(): string | null {
    if (this.view === 'change') return 'change_address';
    return this.signingIn ? 'login' : null;
  }

  /** Opens at the address step, signing in by itself if this tab has `?as=` and the server is in test mode. */
  open(note = ''): void {
    const auto = !this.autoDone && !note;
    this.autoDone = true;
    if (this.isOpen && this.signingIn) return this.setNote(note);
    analytics.track('sign_in_viewed', { had_note: !!note });
    this.show('address');
    this.setNote(note);
    void this.prepare(auto);
  }

  openAccount(email: string): void {
    this.accountEmail.textContent = email;
    this.devices.replaceChildren();
    this.signOutOthersButton.hidden = true;
    this.accountNews.hidden = true;
    this.show('account');
    this.closeButton.focus();
    void this.loadAccount();
  }

  private async loadAccount(): Promise<void> {
    const load = ++this.accountLoads;
    try {
      const account = await api.account();
      if (load === this.accountLoads && this.isOpen && this.view === 'account') this.showAccount(account);
    } catch (e) {
      if (load !== this.accountLoads || !this.isOpen || this.view !== 'account') return;
      this.accountTrouble(e);
    }
  }

  /** For anything that needs the session. */
  private accountTrouble(e: unknown): void {
    this.setBusy(false);
    // Another device signed this one out.
    if (e instanceof ApiError && e.status === 401) return this.open('You were signed out. Sign in to carry on.');
    // The first code is used up, and only a new one can carry the change on.
    if (e instanceof ApiError && e.code === 'start_again') this.backToAccount();
    this.setError(errorText(e));
    if (this.view === 'account') this.closeButton.focus();
  }

  private showAccount(account: AccountInfo): void {
    if (account.email) this.accountEmail.textContent = account.email;
    const now = Date.now();
    // This device goes first, and the server has already put the rest in order of use.
    const sessions = [...account.sessions].sort((a, b) => Number(b.id === account.current) - Number(a.id === account.current));
    this.devices.replaceChildren(...sessions.map((s) => el('li', {}, `${s.label}, ${s.id === account.current ? 'this device' : activeText(s.last_used_at, now)}`)));
    this.signOutOthersButton.hidden = sessions.length < 2;
  }

  close(): void {
    this.overlay.hidden = true;
    input.blocked = false;
    this.stopResendTimer();
    this.setBusy(false);
    this.setError('');
    this.setNote('');
    this.addressInput.value = '';
    this.codeInput.value = '';
    this.clearManaging();
    this.removeCheck();
  }

  private clearManaging(): void {
    for (const field of [this.changeCodeInput, this.newEmailInput, this.confirmCodeInput, this.deleteCodeInput, this.deleteWordInput]) field.value = '';
    this.alsoSignOut.checked = true;
    this.newEmail = '';
  }

  /** Back from changing the address or deleting, with the keyboard somewhere that Escape still works. */
  private backToAccount(): void {
    this.clearManaging();
    this.setView('account');
    this.closeButton.focus();
    // What's on the server may have moved on from what this panel last saw, for instance after a lost reply.
    void this.loadAccount();
  }

  private show(view: View): void {
    this.overlay.hidden = false;
    input.blocked = true;
    this.setView(view);
  }

  private setView(view: View): void {
    this.view = view;
    this.title.textContent = TITLES[view];
    this.addressForm.hidden = view !== 'address';
    this.codeForm.hidden = view !== 'code';
    this.accountView.hidden = view !== 'account';
    this.changeForm.hidden = view !== 'change';
    this.confirmForm.hidden = view !== 'changeCode';
    this.deleteForm.hidden = view !== 'delete';
    if (!this.checkAction) this.removeCheck();
    this.reserveCheck();
    this.setError('');
    if (view !== 'code') this.stopResendTimer();
    if (view === 'address') this.addressInput.focus();
    if (view === 'code') this.codeInput.focus();
    if (view === 'change') this.changeCodeInput.focus();
    if (view === 'changeCode') this.confirmCodeInput.focus();
    if (view === 'delete') this.deleteCodeInput.focus();
  }

  /** The panel is centred, so the check arriving would otherwise move every button just as someone reaches for one. */
  private reserveCheck(): void {
    this.check.classList.toggle('reserved', !!this.checkAction && !!this.config?.turnstileSiteKey);
  }

  private setNote(text: string): void {
    this.note.textContent = text;
    this.note.hidden = !text;
  }

  private setError(text: string): void {
    this.error.textContent = text;
    this.error.hidden = !text;
  }

  private setBusy(busy: boolean): void {
    this.busy = busy;
    for (const b of [
      this.sendButton,
      this.testButton,
      this.verifyButton,
      this.differentButton,
      this.changeAddressButton,
      this.signOutOthersButton,
      this.signOutButton,
      this.deleteAccountButton,
      this.changeSendButton,
      this.changeCancelButton,
      this.confirmButton,
      this.confirmCancelButton,
      this.deleteButton,
      this.deleteCancelButton,
    ]) {
      b.disabled = busy;
    }
  }

  private async loadConfig(): Promise<void> {
    if (this.config) return;
    try {
      this.config = await api.config();
      this.testButton.hidden = !this.config.testMode;
      this.reserveCheck();
    } catch (e) {
      this.setError(errorText(e));
    }
  }

  private async prepare(auto: boolean): Promise<void> {
    await this.loadConfig();
    const as = testIdentity();
    if (auto && as && this.config?.testMode) await this.signInAsTest(`${as}${TEST_DOMAIN}`);
    if (this.isOpen) void this.showCheck();
  }

  private isTestAddress(email: string): boolean {
    return !!this.config?.testMode && email.toLowerCase().endsWith(TEST_DOMAIN);
  }

  private async showCheck(): Promise<void> {
    const sitekey = this.config?.turnstileSiteKey;
    const action = this.checkAction;
    if (!sitekey || !action || (this.widget && this.widgetAction === action)) return;
    // A token is only good for the action it was made for.
    this.removeCheck();
    try {
      const turnstile = await loadTurnstile();
      // The panel may have closed or moved on while the script loaded.
      if (!this.isOpen || this.widget || this.checkAction !== action) return;
      this.widgetAction = action;
      this.widget = turnstile.render(this.check, {
        sitekey,
        action,
        theme: 'dark',
        callback: (token: string) => (this.turnstileToken = token),
        'expired-callback': () => (this.turnstileToken = null),
        'error-callback': () => (this.turnstileToken = null),
      });
    } catch {
      analytics.track('sign_in_failed', { stage: 'robot_check', code: 'script_blocked' });
      this.setError(this.config?.testMode ? `The robot check couldn't load. Addresses ending in ${TEST_DOMAIN} still work.` : "The robot check couldn't load. Check your connection, or anything that blocks scripts.");
    }
  }

  /** Each token works once, so the widget needs a new one after every try. */
  private async resetCheck(): Promise<void> {
    this.turnstileToken = null;
    if (this.widget) (await loadTurnstile()).reset(this.widget);
  }

  private removeCheck(): void {
    this.turnstileToken = null;
    const widget = this.widget;
    this.widget = null;
    if (widget) void loadTurnstile().then((turnstile) => turnstile.remove(widget));
  }

  private async send(email: string): Promise<void> {
    if (this.busy) return;
    // Only to spare a robot check on an obvious typo. The server has the final word.
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      this.setError("That doesn't look like an email address.");
      return;
    }
    this.setBusy(true);
    this.setError('');
    await this.loadConfig();
    const needsCheck = !this.isTestAddress(email);
    if (needsCheck && this.config?.turnstileSiteKey && !this.turnstileToken) {
      analytics.track('sign_in_failed', { stage: 'send_code', code: 'robot_check_unfinished' });
      this.setBusy(false);
      this.setError('Finish the robot check first.');
      return;
    }
    try {
      const resend = email === this.email;
      this.showCodeStep(email, await api.askForCode(email, needsCheck ? this.turnstileToken : null));
      analytics.track('sign_in_code_sent', { resend });
    } catch (e) {
      analytics.track('sign_in_failed', { stage: 'send_code', ...failure(e) });
      this.setError(errorText(e));
    }
    this.setBusy(false);
    if (needsCheck) void this.resetCheck();
  }

  private showCodeStep(email: string, sent: { resendIn: number; testCode?: string }): void {
    this.email = email;
    this.sentTo.textContent = email;
    this.codeInput.value = '';
    this.setTestCode(email, sent.testCode);
    this.setView('code');
    this.startResendTimer(sent.resendIn);
  }

  private setTestCode(email: string, code?: string): void {
    const test = this.isTestAddress(email);
    this.testCode.hidden = !test;
    // A test address can only miss out on a code by having been sent one less than a minute ago.
    if (test) this.testCode.textContent = code ? `TEST MODE: your code is ${code}` : 'TEST MODE: this address had a code less than a minute ago. Send another when the button is ready.';
  }

  private startResendTimer(seconds: number): void {
    this.stopResendTimer();
    let left = seconds;
    const tick = () => {
      this.resendButton.disabled = left > 0;
      this.resendButton.textContent = left > 0 ? `Send again in ${left}` : 'Send code again';
      if (left <= 0) this.stopResendTimer();
      left--;
    };
    tick();
    if (left >= 0) this.resendTimer = setInterval(tick, 1000);
  }

  private stopResendTimer(): void {
    if (this.resendTimer) clearInterval(this.resendTimer);
    this.resendTimer = null;
  }

  private async verify(): Promise<void> {
    if (this.busy) return;
    const code = this.codeInput.value.replace(/\D/g, '');
    if (code.length !== 6) {
      this.setError('The code has 6 digits.');
      return;
    }
    this.setBusy(true);
    this.setError('');
    try {
      const verified = await api.verifyCode(this.email, code);
      this.signInResult = { method: 'email', isNew: !!verified.isNew };
    } catch (e) {
      analytics.track('sign_in_failed', { stage: 'verify_code', ...failure(e) });
      this.setError(errorText(e));
      this.setBusy(false);
      this.codeInput.select();
      return;
    }
    await this.finish();
  }

  /** Signs in through the real calls, with the code the server hands back in test mode. */
  private async signInAsTest(email: string): Promise<void> {
    if (this.busy) return;
    this.setBusy(true);
    this.setError('');
    try {
      const sent = await api.askForCode(email, null);
      // So that the sign-in funnel means the same on staging, where this is how most people get in.
      analytics.track('sign_in_code_sent', { resend: false, test: true });
      if (!sent.testCode) {
        this.showCodeStep(email, sent);
        this.setBusy(false);
        return;
      }
      const verified = await api.verifyCode(email, sent.testCode);
      this.signInResult = { method: 'test', isNew: !!verified.isNew };
    } catch (e) {
      analytics.track('sign_in_failed', { stage: 'test_sign_in', ...failure(e) });
      this.setError(errorText(e));
      this.setBusy(false);
      return;
    }
    await this.finish();
  }

  /** Asks for a code to the account's own address, then moves on to the form that takes it. */
  private async startManaging(view: 'change' | 'delete'): Promise<void> {
    if (this.busy) return;
    this.setBusy(true);
    this.setError('');
    this.accountNews.hidden = true;
    // A page that arrived signed in has had no reason to ask yet.
    await this.loadConfig();
    let sent: OwnCode;
    try {
      sent = await (view === 'change' ? api.startAddressChange() : api.startDelete());
    } catch (e) {
      // The panel may have been closed while that was on its way.
      if (this.isOpen && this.view === 'account') this.accountTrouble(e);
      else this.setBusy(false);
      return;
    }
    this.setBusy(false);
    if (!this.isOpen || this.view !== 'account') return;
    this.clearManaging();
    (view === 'change' ? this.changeSentTo : this.deleteSentTo).textContent = sent.email;
    this.setCodeNote(view === 'change' ? this.changeTest : this.deleteTest, sent.testCode);
    this.setView(view);
    void this.showCheck();
  }

  /** Only a test address gets its code back from the server. */
  private setCodeNote(line: HTMLElement, code?: string): void {
    line.hidden = !code;
    line.textContent = code ? `TEST MODE: your code is ${code}` : '';
  }

  private async sendToNewAddress(): Promise<void> {
    if (this.busy) return;
    const code = digits(this.changeCodeInput);
    const newEmail = this.newEmailInput.value.trim();
    if (code.length !== 6) return this.setError('The code has 6 digits.');
    // Only to spare the code on an obvious typo, as the server has the final word.
    if (!/^\S+@\S+\.\S+$/.test(newEmail)) return this.setError("That doesn't look like an email address.");
    const needsCheck = !this.isTestAddress(newEmail);
    if (needsCheck && this.config?.turnstileSiteKey && !this.turnstileToken) return this.setError('Finish the robot check first.');
    this.setBusy(true);
    this.setError('');
    try {
      const sent = await api.sendToNewAddress(code, newEmail, needsCheck ? this.turnstileToken : null);
      this.setBusy(false);
      this.newEmail = newEmail;
      this.newSentTo.textContent = newEmail;
      this.confirmCodeInput.value = '';
      this.setCodeNote(this.confirmTest, sent.testCode);
      // With no other device listed there's nothing to choose. If one has just signed in, the server still signs it out.
      this.alsoSignOutRow.hidden = this.signOutOthersButton.hidden;
      this.setView('changeCode');
    } catch (e) {
      this.accountTrouble(e);
      if (this.view === 'change') {
        this.changeCodeInput.focus();
        this.changeCodeInput.select();
        if (needsCheck) void this.resetCheck();
      }
    }
  }

  private async confirmNewAddress(): Promise<void> {
    if (this.busy) return;
    const code = digits(this.confirmCodeInput);
    if (code.length !== 6) return this.setError('The code has 6 digits.');
    const signOutOthers = this.alsoSignOut.checked;
    const hadOthers = !this.alsoSignOutRow.hidden;
    const email = this.newEmail.toLowerCase();
    let news = signOutOthers && hadOthers ? 'Your address has changed, and your other devices are signed out.' : 'Your address has changed.';
    this.setBusy(true);
    this.setError('');
    try {
      await api.confirmNewAddress(code, email, signOutOthers);
    } catch (e) {
      // The reply can get lost, or trouble can come just after the move, and asking again would only get "wrong code".
      if (!(await this.addressIs(email))) {
        this.accountTrouble(e);
        if (this.view === 'changeCode') {
          this.confirmCodeInput.focus();
          this.confirmCodeInput.select();
        }
        return;
      }
      // The sign-outs come after the move, so they may not have happened. This may be why the player is changing address.
      if (signOutOthers) {
        try {
          await api.signOutOthers();
        } catch {
          news = "Your address has changed, but we couldn't sign out your other devices. Try again from here.";
        }
      }
    }
    this.setBusy(false);
    this.backToAccount();
    this.accountEmail.textContent = email;
    this.accountNews.textContent = news;
    this.accountNews.hidden = false;
    this.host.accountChanged(email);
  }

  private async addressIs(email: string): Promise<boolean> {
    try {
      return (await api.account()).email === email;
    } catch {
      return false;
    }
  }

  private async confirmDelete(): Promise<void> {
    if (this.busy) return;
    const code = digits(this.deleteCodeInput);
    if (code.length !== 6) return this.setError('The code has 6 digits.');
    if (this.deleteWordInput.value.trim() !== DELETE_WORD) return this.setError(`Type ${DELETE_WORD} to confirm.`);
    this.setBusy(true);
    this.setError('');
    try {
      await api.confirmDelete(code);
    } catch (e) {
      this.accountTrouble(e);
      if (this.view === 'delete') {
        this.deleteCodeInput.focus();
        this.deleteCodeInput.select();
      }
      return;
    }
    // In this order, because the event must not bring back the person that the server is about to delete.
    analytics.reset();
    analytics.track('account_deleted');
    this.autoDone = true;
    this.close();
    this.tabs?.postMessage('out');
    this.host.signedOut('Your account and heroes are deleted.');
  }

  /** Only another request can show whether the browser kept the cookie. */
  private async finish(): Promise<void> {
    try {
      await api.heroes();
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        analytics.track('sign_in_failed', { stage: 'keep_session', code: 'cookies_blocked' });
        this.setError('This game needs cookies to keep you signed in. Allow cookies for this site, then try again.');
        this.setBusy(false);
        return;
      }
      // Any other trouble is for the hero list to show.
    }
    analytics.track('sign_in_completed', { method: this.signInResult?.method, is_new_account: !!this.signInResult?.isNew });
    this.signInResult = null;
    this.close();
    this.tabs?.postMessage('in');
    this.host.signedIn();
  }

  private async signOutOthers(): Promise<void> {
    if (this.busy) return;
    this.setBusy(true);
    this.setError('');
    try {
      await api.signOutOthers();
    } catch (e) {
      this.accountTrouble(e);
      return;
    }
    this.setBusy(false);
    this.accountNews.textContent = 'Your other devices are signed out.';
    this.accountNews.hidden = false;
    // The button that had the keyboard is about to go away.
    this.closeButton.focus();
    await this.loadAccount();
  }

  private async signOut(): Promise<void> {
    if (this.busy) return;
    this.setBusy(true);
    try {
      await api.signOut();
    } catch (e) {
      this.setError(errorText(e));
      this.setBusy(false);
      return;
    }
    analytics.track('signed_out');
    this.autoDone = true;
    this.close();
    this.tabs?.postMessage('out');
    this.host.signedOut();
  }
}
